import React, { useEffect, useState } from 'react';
import { Accordion, Alert, Badge, Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket } from '@fortawesome/free-solid-svg-icons';
import { callQuery } from '../../amplify/api-gateway/query';
import { GET_RELEASE_NOTES, ReleaseNote } from '../../amplify/api-gateway/query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const ReleaseNotesPage: React.FC = () => {
    const [notes, setNotes] = useState<ReleaseNote[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        fetchNotes();
    }, []);

    const fetchNotes = async () => {
        setLoading(true);
        setError(false);
        try {
            const result = await callQuery({ operation: GET_RELEASE_NOTES });
            setNotes(result || []);
        } catch (err) {
            console.error('Error fetching release notes:', err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (timestamp: number) => {
        return new Date(timestamp).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    return (
        <Container fluid style={{ marginTop: '80px' }}>
            <Row className="justify-content-center">
                <Col lg={8}>
                    <div className="mb-4">
                        <h4>
                            <FontAwesomeIcon icon={faRocket} /> Release Notes
                        </h4>
                        <p className="text-muted">
                            Stay up to date with the latest features, improvements, and bug fixes.
                        </p>
                    </div>

                    {loading ? (
                        <Card>
                            <Card.Body>
                                <div className="text-center p-4">
                                    <Spinner animation="border" />
                                    <p className="mt-2">Loading release notes...</p>
                                </div>
                            </Card.Body>
                        </Card>
                    ) : error ? (
                        <Alert variant="danger">
                            Failed to load release notes. Please try refreshing the page.
                        </Alert>
                    ) : notes.length === 0 ? (
                        <Alert variant="info">
                            No release notes available at this time.
                        </Alert>
                    ) : (
                        <Accordion>
                            {notes && Array.isArray(notes) && notes.map((note) => (
                                <Accordion.Item
                                    eventKey={note.createdAt.toString()}
                                    key={note.createdAt}
                                >
                                    <Accordion.Header>
                                        <div className="d-flex align-items-center w-100">
                                            <Badge bg="secondary" className="me-2">
                                                {note.version}
                                            </Badge>
                                            <strong className="me-auto">{note.title}</strong>
                                            <small className="text-muted ms-2">
                                                {formatDate(note.createdAt)}
                                            </small>
                                        </div>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {note.description}
                                        </ReactMarkdown>
                                        {note.updatedAt && (
                                            <small className="text-muted d-block mt-3">
                                                Last updated: {formatDate(note.updatedAt)}
                                            </small>
                                        )}
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    )}
                </Col>
            </Row>
        </Container>
    );
};
